import type { PanelHandle } from '../scene/panels';
import type {
  ConceptElement,
  SystemElement,
} from '../data/types';

export type FilterKey =
  | 'foundational'
  | 'formal'
  | 'diagnostic'
  | 'derived'
  | 'claim'
  | 'phenomenon'
  | 'paper';

const FILTER_KEYS: FilterKey[] = [
  'foundational', 'formal', 'diagnostic', 'derived',
  'claim', 'phenomenon', 'paper',
];

function elementMatches(el: SystemElement, key: FilterKey): boolean {
  switch (key) {
    case 'foundational': return el.kind === 'concept' && (el as ConceptElement).group === 'foundational';
    case 'formal':       return el.kind === 'concept' && (el as ConceptElement).group === 'formal';
    case 'diagnostic':   return el.kind === 'concept' && (el as ConceptElement).group === 'diagnostic';
    case 'derived':      return el.kind === 'concept' && (el as ConceptElement).group === 'derived';
    case 'claim':        return el.kind === 'claim';
    case 'phenomenon':   return el.kind === 'phenomenon';
    case 'paper':        return el.kind === 'paper';
  }
}

export class FilterManager {
  private active = new Set<FilterKey>();
  private panels: PanelHandle[];
  private rows: Map<FilterKey, HTMLButtonElement>;
  private clearBtn: HTMLElement;

  onChange?: (active: ReadonlySet<FilterKey>) => void;

  constructor(panels: PanelHandle[], legendRoot: HTMLElement, clearBtn: HTMLElement) {
    this.panels = panels;
    this.clearBtn = clearBtn;

    this.rows = new Map();
    for (const key of FILTER_KEYS) {
      const btn = legendRoot.querySelector<HTMLButtonElement>(`[data-filter="${key}"]`);
      if (!btn) continue;
      this.rows.set(key, btn);
      // Stop pointerdown bubbling to OrbitControls; otherwise its
      // setPointerCapture call swallows the click event.
      btn.addEventListener('pointerdown', (e) => e.stopPropagation());
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggle(key);
      });
    }

    clearBtn.addEventListener('pointerdown', (e) => e.stopPropagation());
    clearBtn.addEventListener('click', () => this.clear());
  }

  toggle(key: FilterKey) {
    if (this.active.has(key)) this.active.delete(key);
    else this.active.add(key);
    this.apply();
  }

  clear() {
    if (this.active.size === 0) return;
    this.active.clear();
    this.apply();
  }

  isFiltered(): boolean {
    return this.active.size > 0;
  }

  matches(el: SystemElement): boolean {
    if (this.active.size === 0) return true;
    // Feature + domains are anchors — always at full opacity.
    if (el.kind === 'feature' || el.kind === 'domain') return true;
    for (const k of this.active) {
      if (elementMatches(el, k)) return true;
    }
    return false;
  }

  private apply() {
    for (const p of this.panels) {
      const matched = this.matches(p.element);
      p.domElement.classList.toggle('filtered', !matched);
    }

    for (const [key, btn] of this.rows) {
      btn.classList.toggle('active', this.active.has(key));
      btn.setAttribute('aria-pressed', this.active.has(key) ? 'true' : 'false');
    }

    if (this.active.size > 0) {
      this.clearBtn.removeAttribute('hidden');
    } else {
      this.clearBtn.setAttribute('hidden', '');
    }

    this.onChange?.(this.active);
  }
}
